/**
 * The battle page's stage machine, framework-free like the opener's `createOpenerMachine`:
 *
 *   waiting → countdown → live(round, phase) → completed
 *
 * Server data is canonical; this only decides what the page shows and when. A round advances
 * only once its animation has finished AND the server has revealed the next one; a viewer who
 * watched live is never fast-forwarded because the server finished first; a direct load of a
 * completed battle jumps to the results; replay re-runs the choreography from the countdown on
 * the full results without consulting the server cursor.
 */
import type { BattleStatus } from "@loot-os/api-contract";
import { useEffect, useRef, useSyncExternalStore } from "react";

export type BattleStage = "waiting" | "countdown" | "live" | "completed";
/** `waitingData`: the round's results have not arrived; `spinning`: the reel runs; `showing`: results shown, hold running. */
export type BattleRoundPhase = "waitingData" | "spinning" | "showing";

export interface BattleMachineState {
  stage: BattleStage;
  /** The round the live stage is on; -1 before round 0. */
  round: number;
  roundPhase: BattleRoundPhase;
  /** The viewer saw the countdown or a live round, so completion must not skip the animation. */
  watchedLive: boolean;
  replaying: boolean;
  /** Set by `replay()`: the local instant the replay's countdown began. */
  replayStartedAtMs: number | null;
}

/** What the machine needs from the server's battle. */
export interface BattleMachineSnapshot {
  status: BattleStatus;
  currentRevealedRound: number;
  roundCount: number;
  startedAtMs: number | null;
  startCountdownMs: number;
}

export interface BattleMachine {
  getState(): BattleMachineState;
  subscribe(listener: () => void): () => void;
  /** Apply the latest server view; call on every fetch and event. */
  syncServer(snapshot: BattleMachineSnapshot): void;
  countdownFinished(): void;
  roundAnimationFinished(): void;
  holdFinished(): void;
  replay(): void;
  /** Resync after a tab-out: jump straight into round `round`. */
  snapTo(round: number): void;
}

const INITIAL: BattleMachineState = {
  stage: "waiting",
  round: -1,
  roundPhase: "waitingData",
  watchedLive: false,
  replaying: false,
  replayStartedAtMs: null,
};

export function createBattleMachine(deps: { now: () => number }): BattleMachine {
  let state = INITIAL;
  let snapshot: BattleMachineSnapshot | null = null;
  const listeners = new Set<() => void>();

  function set(next: BattleMachineState) {
    if (next === state) return;
    state = next;
    for (const listener of listeners) listener();
  }

  /** Can round N spin now: its data is here, or a replay has everything. */
  const dataReady = (round: number) =>
    state.replaying || (snapshot !== null && snapshot.currentRevealedRound >= round);

  return {
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    syncServer(next) {
      snapshot = next;
      if (state.replaying) return;
      if (state.stage === "waiting") {
        if (next.status === "created") return;
        if (next.status === "started" && next.startedAtMs !== null) {
          const goMs = next.startedAtMs + next.startCountdownMs;
          if (deps.now() < goMs) {
            set({ ...state, stage: "countdown", watchedLive: true });
            return;
          }
          const timelineRound = Math.min(
            Math.max(0, Math.floor((deps.now() - goMs) / Math.max(1, intervalOf(next)))),
            next.roundCount - 1,
          );
          set({
            ...state,
            stage: "live",
            round: timelineRound,
            roundPhase: next.currentRevealedRound >= timelineRound ? "spinning" : "waitingData",
            watchedLive: true,
          });
          return;
        }
        if (next.status === "completed") {
          set({ ...state, stage: "completed" });
        }
        return;
      }
      if (
        state.stage === "live" &&
        state.roundPhase === "waitingData" &&
        next.currentRevealedRound >= state.round
      ) {
        set({ ...state, roundPhase: "spinning" });
      }
      // countdown/live with the battle already completed on the server: keep animating.
    },
    countdownFinished() {
      if (state.stage !== "countdown") return;
      set({
        ...state,
        stage: "live",
        round: 0,
        roundPhase: dataReady(0) ? "spinning" : "waitingData",
      });
    },
    roundAnimationFinished() {
      if (state.stage !== "live" || state.roundPhase !== "spinning") return;
      set({ ...state, roundPhase: "showing" });
    },
    holdFinished() {
      if (state.stage !== "live" || state.roundPhase !== "showing") return;
      const roundCount = snapshot?.roundCount ?? 0;
      const nextRound = state.round + 1;
      if (nextRound >= roundCount) {
        set({ ...state, stage: "completed", replaying: false, replayStartedAtMs: null });
        return;
      }
      set({
        ...state,
        round: nextRound,
        roundPhase: dataReady(nextRound) ? "spinning" : "waitingData",
      });
    },
    replay() {
      if (snapshot?.status !== "completed") return;
      set({
        ...state,
        stage: "countdown",
        round: -1,
        roundPhase: "waitingData",
        replaying: true,
        replayStartedAtMs: deps.now(),
      });
    },
    snapTo(round) {
      set({ ...state, stage: "live", round, roundPhase: "spinning" });
    },
  };
}

/** The machine keeps no interval of its own; the page's timeline math owns pacing. This is only
 * for the direct-load case, where the page has not rendered yet. */
function intervalOf(
  snapshot: BattleMachineSnapshot & { revealIntervalMs?: number | null },
): number {
  return snapshot.revealIntervalMs ?? 5800;
}

/** One machine per mounted page, synced from the latest snapshot. */
export function useBattleMachine(
  snapshot: (BattleMachineSnapshot & { revealIntervalMs?: number | null }) | null,
  now: () => number,
): { machine: BattleMachine; state: BattleMachineState } {
  const ref = useRef<BattleMachine | null>(null);
  if (!ref.current) ref.current = createBattleMachine({ now });
  const machine = ref.current;
  const state = useSyncExternalStore(machine.subscribe, machine.getState, machine.getState);
  useEffect(() => {
    if (snapshot) machine.syncServer(snapshot);
  }, [machine, snapshot]);
  return { machine, state };
}
